import { CollectedItemDetails, MagicCard } from "@apiserver-rest/ponder-data-client-base";
import { FilteredPreviewItem } from "@ponder-shared/tools/filter/filtered-preview-item";
import { FilterType, PreviewItemFilterOption } from "@ponder-shared/tools/filter/preview-item-filter-option";

enum MagicCardDefaultType {
    All = "All Types",
    NonLand = "Nonland",
    Creature = "Creature",
    Artifact = "Artifact",
    Enchantment = "Enchantment",
    Land = "Land",
    Instant = "Instant",
    Sorcery = "Sorcery",
}

export class MagicCardFilterType extends PreviewItemFilterOption {
    private currentChoice: string = "All Types";
    private filters = new Map<string, number | undefined>();
    private isDirty: boolean = true;
    private allValid: boolean = true;

    constructor() {
        super("Type", FilterType.Select, "", MagicCardDefaultType.All, MagicCardDefaultType.All);
    }

    public prepare(): void {
        this.filters.clear();

        Object.values(MagicCardDefaultType).forEach(o => {
            if (o != MagicCardDefaultType.All) {
                this.filters.set(o, 0);
            }
        });
    }

    public track(magicCard: MagicCard, collectedInfo: CollectedItemDetails | undefined): void {
        var types = magicCard.faces![0].typeLine;
        var backTypes = (magicCard.faces!.length > 1 ? magicCard.faces![1].typeLine : undefined);
        
        if (!types) {
            return;
        }

        if (types && backTypes) {
            types += " " + backTypes;
        }

        var individualTypes = types?.split(' ');

        for (let i = 0; i < individualTypes.length; ++i) {
            let type = individualTypes[i];

            if (type === "—") {
                continue;
            }

            let known = this.filters.get(type) ?? 0;

            this.filters.set(type, known + 1);
            this.isDirty = true;
        }

        if (types && this.isNonLand(types)) {
            var nonlandCount = this.filters.get(MagicCardDefaultType.NonLand) ?? 0;
            this.filters.set(MagicCardDefaultType.NonLand, nonlandCount + 1);
            this.isDirty = true;
        }
    }

    public updateSelection(selection: string, page: string): boolean {
        if (this.currentChoice === selection) {
            return false;
        }

        this.setStoredFilter(selection, page);
        this.currentChoice = selection;
        this.allValid = (this.currentChoice === "All Types");
        this.wasUpdated = true;

        return true;
    }

    public getFilters(): Map<string, number | undefined> {
        this.sortFilters();
        return this.filters;
    }

    private sortFilters(): void {
        if (!this.isDirty) {
            return;
        }

        var sorted = new Map([...this.filters.entries()]
            .sort((a: [ string, number | undefined ], b: [string, number | undefined]) => {
                var aIsDefault = Object.values(MagicCardDefaultType).includes(a[0] as MagicCardDefaultType);
                var bIsDefault = Object.values(MagicCardDefaultType).includes(b[0] as MagicCardDefaultType);

                if (aIsDefault == bIsDefault) {
                    return a[0] < b[0] ? -1 : 1;
                } else if (aIsDefault) {
                    return -1;
                } else {
                    return 1;
                }
            }));

        this.filters.clear();
        this.filters.set("All Types", undefined);
        
        sorted.forEach((value: number | undefined, key: string, _) => {
            this.filters.set(key, value);
        });

        this.isDirty = false;
    }

    public isValid(magicCard: MagicCard, collectedInfo: CollectedItemDetails | undefined): boolean {
        if (this.allValid) {
            return true;
        }

        if (this.currentChoice == MagicCardDefaultType.NonLand) {
            for (let i = 0; i < magicCard.faces!.length; ++i) {
                let face = magicCard.faces![i];
    
                if (face.typeLine && this.isNonLand(face.typeLine)) {
                    return true;
                }
            }
        } else {
            for (let i = 0; i < magicCard.faces!.length; ++i) {
                let face = magicCard.faces![i];
    
                if (face.typeLine) {
                    // Can't just check if the type is in the type line. That leads to false positives.
                    // For example, selecting "Bat" would then also show "Battles".
                    // Need to check if it is at the end of the line, or that there is a space after it.
                    var startIndex = face.typeLine.indexOf(this.currentChoice);
                    var endIndex = startIndex + this.currentChoice.length;
                    
                    return (startIndex >= 0) && ((endIndex >= face.typeLine.length) || (face.typeLine.charAt(endIndex) === " "));
                }
            }
        }
        

        return false;
    }

    private isNonLand(type: string): boolean {
        return type.indexOf("Land") < 0;
    }

    public sort(previewItems: FilteredPreviewItem[]): void {
        // ... nothing ...
    }

    public override setUserInfo(isCollection: boolean, isAuthed: boolean): void {
        // ... nothing ...
    }
}